import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, ShieldCheck, Lock, CreditCard, HelpCircle, BookOpen, ChefHat, ArrowRight, MessageCircle, Utensils } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const HelpCenterPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="help-center-page">
      <Navbar />
      <div className="h-20" />

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#FDF8F4] to-[#F4EDE4] py-16 px-6 md:px-10 lg:px-20">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3">Help Center</h1>
          <p className="text-lg text-gray-600">Everything you need to know about MealHoster</p>
        </div>
      </div>

      {/* Trust / Care-free Section */}
      <section className="bg-[#f7fbf7] py-16 px-6 md:px-10 lg:px-20" data-testid="trust-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-2">
            Enjoy MealHoster care-free<span className="text-[#D65A31]">!</span>
          </h2>
          <p className="text-center text-gray-500 mb-12 max-w-xl mx-auto">
            We've built every layer of our platform with your safety and peace of mind at the centre.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 text-center">
            <div className="flex flex-col items-center">
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <Globe className="w-12 h-12 text-gray-800 stroke-[1.2]" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Host Protection Guarantee</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Every MealHoster experience is backed by our host protection programme — giving you confidence at no extra cost.
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <ShieldCheck className="w-12 h-12 text-gray-800 stroke-[1.2]" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Verified & Vetted Hosts</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Our hosts go through a thorough application and verification process before they can list an experience.
              </p>
            </div>

            <div className="flex flex-col items-center">
              <div className="w-16 h-16 mb-4 flex items-center justify-center">
                <Lock className="w-12 h-12 text-gray-800 stroke-[1.2]" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Secure Transactions</h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                All payments are processed securely online. Your financial data is encrypted and protected at every step.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Topics */}
      <section className="py-16 px-6 md:px-10 lg:px-20" data-testid="faq-section">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10">
            Frequently asked questions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              {
                icon: BookOpen,
                title: 'Booking & Reservations',
                items: [
                  'How do I book an experience?',
                  'Can I book for a group?',
                  'What happens after I reserve?',
                  'How do I change or cancel a booking?',
                ],
              },
              {
                icon: CreditCard,
                title: 'Payments & Refunds',
                items: [
                  'When am I charged?',
                  'What payment methods are accepted?',
                  'How do refunds work?',
                  'Is there a service fee?',
                ],
              },
              {
                icon: ChefHat,
                title: 'Hosting',
                items: [
                  'How do I become a host?',
                  'How do I set my prices?',
                  'When do I get paid?',
                  'Can I manage my availability?',
                ],
              },
              {
                icon: Utensils,
                title: 'Dietary & Allergies',
                items: [
                  'Can I specify dietary requirements?',
                  'How do I inform my host about allergies?',
                  'Are allergen-free options available?',
                  'What if my needs aren\'t listed?',
                ],
              },
              {
                icon: ShieldCheck,
                title: 'Safety & Trust',
                items: [
                  'How are hosts verified?',
                  'What safety measures are in place?',
                  'How do I report a concern?',
                  'Is my personal data secure?',
                ],
              },
              {
                icon: HelpCircle,
                title: 'Account & Profile',
                items: [
                  'How do I create an account?',
                  'How do I reset my password?',
                  'Can I edit my profile?',
                  'How do I delete my account?',
                ],
              },
            ].map((section) => (
              <div key={section.title} className="bg-gray-50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <section.icon className="w-5 h-5 text-[#D65A31]" />
                  <h3 className="font-bold text-gray-900">{section.title}</h3>
                </div>
                <ul className="space-y-2.5">
                  {section.items.map((item) => (
                    <li key={item} className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-300 mt-0.5">&#8250;</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / Need Help CTA */}
      <section className="mx-6 md:mx-10 lg:mx-20 mb-16" data-testid="contact-section">
        <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden flex flex-col md:flex-row min-h-[320px]">
          {/* Left — text */}
          <div className="bg-[#D65A31] text-white p-10 md:p-14 flex flex-col justify-center md:w-3/5">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Need help or have a question<span className="text-[#FDF8F4]">?</span>
            </h2>
            <p className="text-white/90 leading-relaxed mb-8 max-w-md">
              We're dedicated to making your MealHoster experience exceptional. For any assistance — from booking enquiries to on-the-ground support — please email us. Our team is available 7 days a week to help with everything, including personalised recommendations.
            </p>
            <div>
              <a
                href="mailto:help@mealhoster.com"
                className="inline-flex items-center gap-3 bg-white text-gray-900 font-semibold pl-3 pr-6 py-3 rounded-full hover:shadow-lg transition-shadow"
                data-testid="contact-btn"
              >
                <span className="w-9 h-9 bg-[#D65A31] rounded-full flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-white" />
                </span>
                get in touch with our team
              </a>
            </div>
          </div>
          {/* Right — image */}
          <div className="md:w-2/5 h-56 md:h-auto">
            <img
              src="https://images.pexels.com/photos/3938961/pexels-photo-3938961.jpeg?auto=compress&cs=tinysrgb&w=800&h=600&fit=crop"
              alt="Friends enjoying a candlelit dinner together"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="bg-gray-50 py-12 px-6 md:px-10 lg:px-20" data-testid="quick-links">
        <div className="max-w-5xl mx-auto">
          <h3 className="font-bold text-gray-900 text-center mb-6">Quick links</h3>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: 'How it works', to: '/how-it-works' },
              { label: 'Privacy Policy', to: '/privacy' },
              { label: 'About Us', to: '/about' },
              { label: 'Host an experience', to: '/host' },
              { label: 'Browse experiences', to: '/' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-5 py-2 bg-white rounded-full text-sm font-medium text-gray-700 border hover:border-[#D65A31] hover:text-[#D65A31] transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpCenterPage;
